import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import {
  CreateFormHeader,
  TemplatesSidebar,
  FormContentArea,
  FormComponentsSidebar,
  TEMPLATE_CATEGORIES,
  type SelectedTemplateItem,
  type FormComponentType,
  type TemplateCategory,
  type SavedTemplateFormData,
  type GroupField,
  type FormBuilderSelection,
  type FieldSettingsUpdateHandler,
} from '@components/pages/FormBuilder'
import {
  createFormTemplate,
  createFormTemplateVersion,
  updateFormTemplateStatus,
  deleteFormTemplate,
} from '@services/api/forms.api'
import { schemaJsonToGroups } from '@components/pages/FormBuilder/SendingFormsTable/formTemplateToFormData'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createFormTemplateApprovedToast,
  createFormTemplateCreatedToast,
  createFormTemplateDeletedToast,
  createFormTemplateUpdatedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { FormTemplateStatus } from '@dataTypes/forms.type'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  fetchFormTemplateById,
  fetchFormTemplates,
} from '@store/actions/forms.action'
import { Alert, Modal } from '@enamelai/design-system'
import SelectPatientModal from '@components/layout/Modals/SelectPatient/SelectPatient.component'
import AssignFormsModal from '@components/pages/FormBuilder/SendingFormsTable/AssignFormsModal.component'
import type { PendingFormEntry } from '@pages/FormBuilder/assignedForms.types'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import { getFullName } from '@utils/fullname.util'
import type { Patient } from '@dataTypes/PatientInfo.types'
import type { IContact } from '@dataTypes/contact.type'
import './CreateForm.css'

interface CreateFormLocationState {
  formTitle?: string
  templateId?: string
  categoryTitle?: string
}

const GLOBAL_LOADING_CONTAINER = 'body'

interface CreateFormProps {
  onFormsAssigned?: (entry: PendingFormEntry) => void
}

const CreateForm: React.FC<CreateFormProps> = ({ onFormsAssigned }) => {
  const dispatch = useAppDispatch()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedContact = useAppSelector(
    (state) => state.patient.selectedContact,
  )
  const location = useLocation()
  const navigate = useNavigate()
  const { templateId: routeTemplateId } = useParams<{ templateId: string }>()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null,
  )
  const [templateLoadError, setTemplateLoadError] = useState<string | null>(
    null,
  )
  const [templateCategories, setTemplateCategories] = useState<
    TemplateCategory[]
  >(() => TEMPLATE_CATEGORIES.map((cat) => ({ ...cat, items: [] })))
  const [initialFormTitle, setInitialFormTitle] = useState<string | null>(
    () =>
      (location.state as CreateFormLocationState | undefined)?.formTitle ??
      null,
  )
  const [templateSearch, setTemplateSearch] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedTemplateItem, setSelectedTemplateItem] =
    useState<SelectedTemplateItem | null>(null)
  const [formBuilderKey, setFormBuilderKey] = useState(0)
  const [selectedField, setSelectedField] =
    useState<FormBuilderSelection | null>(null)
  const selectedFieldId = selectedField?.id ?? null
  const [isFormDirty, setIsFormDirty] = useState(false)
  const onFormDataChange = useCallback(() => {
    setIsFormDirty(true)
  }, [])

  useEffect(() => {
    setSelectedField(null)
    setIsFormDirty(false)
  }, [routeTemplateId, selectedTemplateItem?.name, formBuilderKey])
  const componentClickRef = useRef<
    ((component: FormComponentType) => void) | null
  >(null)
  const fieldSettingsRef = useRef<FieldSettingsUpdateHandler | null>(null)
  const handleFieldSelect = useCallback(
    (field: FormBuilderSelection | null): void => {
      setSelectedField(field)
    },
    [],
  )
  const handleFieldSettingsChange: FieldSettingsUpdateHandler = useCallback(
    (fieldId, updates) => {
      fieldSettingsRef.current?.(fieldId, updates)
    },
    [],
  )
  const previewToggleRef = useRef<{
    isPreviewMode: boolean
    togglePreview: () => void
  } | null>(null)
  const formDataRef = useRef<
    | (() => {
        formTitle: string
        groups: GroupField[]
        formStatus: FormTemplateStatus
      })
    | null
  >(null)
  const [savedTemplateFormData, setSavedTemplateFormData] = useState<
    Record<string, SavedTemplateFormData>
  >({})
  const loadedTemplateContextRef = useRef<string | null>(null)
  /** When true, edit template has loaded (or we're not in edit mode). Used to avoid showing empty form before load. */
  const [editTemplateLoadSettled, setEditTemplateLoadSettled] = useState(false)
  const [showUnsavedConfirmModal, setShowUnsavedConfirmModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<
    (() => void | (() => void)) | null
  >(null)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const [showSelectPatientModal, setShowSelectPatientModal] = useState(false)
  const [showAssignFormsModal, setShowAssignFormsModal] = useState(false)
  type BuilderMode = 'create' | 'edit' | 'view'
  const [mode, setMode] = useState<BuilderMode>(() => {
    if (routeTemplateId) {
      return location.pathname.includes('/form-builder/view') ? 'view' : 'edit'
    }
    return 'create'
  })
  const isViewMode = mode === 'view'
  const selectedPatientName = getFullName(selectedPatient)
  const activeTemplateId = editingTemplateId ?? routeTemplateId

  const handleSelectPatientForAssignment = (
    patient: Patient,
    contact?: IContact,
  ): void => {
    dispatch(setSelectedPatient({ patient, contact }))
    setShowSelectPatientModal(false)
    setShowAssignFormsModal(true)
  }

  const handleSendToPatient = (): void => {
    if (!selectedPatient?.patient_id) {
      setShowSelectPatientModal(true)
      return
    }
    setShowAssignFormsModal(true)
  }

  useEffect(() => {
    if (!routeTemplateId) {
      setEditingTemplateId(null)
      setEditTemplateLoadSettled(true)
      loadedTemplateContextRef.current = null
      setMode('create')
    }
  }, [routeTemplateId])

  useEffect(() => {
    if (routeTemplateId) {
      setMode(
        location.pathname.includes('/form-builder/view') ? 'view' : 'edit',
      )
    }
  }, [routeTemplateId, location.pathname])

  // Show global loading before paint when navigating to edit so there's no blank delay
  useLayoutEffect(() => {
    if (routeTemplateId) {
      showLoading({ container: GLOBAL_LOADING_CONTAINER })
    }
  }, [routeTemplateId, showLoading])

  useEffect(() => {
    if (!routeTemplateId) return
    const loadContext = `${routeTemplateId}|${mode}`
    if (loadedTemplateContextRef.current === loadContext) return
    loadedTemplateContextRef.current = loadContext
    setTemplateLoadError(null)
    setEditTemplateLoadSettled(false)
    dispatch(fetchFormTemplateById(routeTemplateId))
      .unwrap()
      .then((template) => {
        if (!template) {
          setTemplateLoadError('Template not found.')
          return
        }
        const title = template.title ?? 'Untitled Form'
        const categoryTitle = template.category_title ?? 'Others'
        const status = template.status ?? FormTemplateStatus.Pending
        let groups: GroupField[] = []
        if (template.schema_json) {
          groups = schemaJsonToGroups(template.schema_json)
        }
        setInitialFormTitle(title)
        setSelectedCategory(categoryTitle)
        setSelectedTemplateItem({ name: title, status })
        setTemplateCategories((prev) => {
          const cat = prev.find((c) => c.title === categoryTitle)
          const itemExists = cat?.items.some((i) => i.name === title)
          if (itemExists) return prev
          return prev.map((c) =>
            c.title === categoryTitle
              ? {
                  ...c,
                  items: [
                    ...c.items,
                    {
                      name: title,
                      status,
                      icon: 'form' as const,
                      form_template_id: template.form_template_id,
                    },
                  ],
                }
              : c,
          )
        })
        setSavedTemplateFormData((prev) => ({
          ...prev,
          [`${categoryTitle}|${title}`]: {
            formTitle: title,
            groups,
            formStatus: status,
          },
        }))
        setEditingTemplateId(template.form_template_id ?? routeTemplateId)
      })
      .catch(() => setTemplateLoadError('Failed to load template.'))
      .finally(() => {
        setEditTemplateLoadSettled(true)
        hideLoading(GLOBAL_LOADING_CONTAINER)
      })
  }, [routeTemplateId, mode, showLoading, hideLoading, dispatch])

  // Load form templates from database and merge into sidebar (templates-list)
  useEffect(() => {
    if (!routeTemplateId) {
      showLoading({ container: GLOBAL_LOADING_CONTAINER })
    }
    dispatch(fetchFormTemplates(undefined))
      .unwrap()
      .then((templates) => {
        const toItem = (
          t: (typeof templates)[number],
        ): TemplateCategory['items'][number] => ({
          name: t.title ?? 'Untitled Form',
          status: t.status ?? FormTemplateStatus.Pending,
          icon: 'form' as const,
          form_template_id: t.form_template_id,
        })
        setTemplateCategories((prev) =>
          prev.map((cat) => {
            const byCategory = templates.filter(
              (t) => t.category_title === cat.title,
            )
            const byTemplateKey = new Map<string, (typeof templates)[number]>()
            for (const t of byCategory) {
              const key = t.template_key ?? t.form_template_id ?? ''
              const existing = byTemplateKey.get(key)
              const ver = t.version ?? 0
              if (!existing || (existing.version ?? 0) < ver) {
                byTemplateKey.set(key, t)
              }
            }
            const latestPerTemplate = Array.from(byTemplateKey.values())
            const byName = new Map<string, (typeof templates)[number]>()
            type TemplateWithUpdated = (typeof templates)[number] & {
              date_time_updated_at?: string
            }
            for (const t of latestPerTemplate) {
              const nameKey = `${cat.title}|${(t.title ?? '').trim().toLowerCase()}`
              const existing = byName.get(nameKey) as
                | TemplateWithUpdated
                | undefined
              const tUpdated =
                (t as TemplateWithUpdated).date_time_updated_at ?? ''
              const existingUpdated = existing?.date_time_updated_at ?? ''
              if (!existing || existingUpdated < tUpdated) {
                byName.set(nameKey, t)
              }
            }
            const onePerName = Array.from(byName.values())
            return {
              ...cat,
              items: onePerName.map(toItem),
            }
          }),
        )
      })
      .catch(() => {
        // Keep current categories with empty items on error
      })
      .finally(() => {
        // When editing a template, loading is hidden by the getFormTemplateById effect
        if (!routeTemplateId) {
          hideLoading(GLOBAL_LOADING_CONTAINER)
        }
      })
  }, [showLoading, hideLoading, routeTemplateId, dispatch])

  const handleCategorySelect = (categoryTitle: string): void => {
    if (categoryTitle === selectedCategory) return
    if (selectedTemplateItem && selectedCategory) {
      setTemplateCategories((prev) => {
        const fromCat = prev.find((c) => c.title === selectedCategory)
        const item = fromCat?.items.find(
          (i) => i.name === selectedTemplateItem?.name,
        )
        if (!item) return prev
        return prev.map((cat) => {
          if (cat.title === selectedCategory) {
            return {
              ...cat,
              items: cat.items.filter(
                (i) => i.name !== selectedTemplateItem.name,
              ),
            }
          }
          if (cat.title === categoryTitle) {
            return { ...cat, items: [...cat.items, { ...item }] }
          }
          return cat
        })
      })
      const oldKey = `${selectedCategory}|${selectedTemplateItem.name}`
      const newKey = `${categoryTitle}|${selectedTemplateItem.name}`
      setSavedTemplateFormData((prev) => {
        const saved = prev[oldKey]
        if (!saved) return prev
        const next = { ...prev }
        delete next[oldKey]
        next[newKey] = saved
        return next
      })
    }
    setSelectedCategory(categoryTitle)
  }

  const shouldShowFormBuilder = (): boolean => {
    // Creating or editing always shows the canvas; the category can be picked there.
    if (!isViewMode) return true
    if (!selectedCategory) return false
    const category = TEMPLATE_CATEGORIES.find(
      (cat) => cat.title === selectedCategory,
    )
    return !!category
  }

  const hasAnyForms = (): boolean =>
    templateCategories.some((cat) => cat.items.length > 0)

  const hasUnsavedChanges = (): boolean => {
    if (!isFormDirty) return false
    const current = formDataRef.current?.()
    if (!current) return false
    if (selectedTemplateItem && selectedCategory) {
      const key = `${selectedCategory}|${selectedTemplateItem.name}`
      const saved = savedTemplateFormData[key]
      if (saved) {
        const groupsMatch =
          JSON.stringify(current.groups) === JSON.stringify(saved.groups)
        return (
          current.formTitle !== saved.formTitle ||
          current.formStatus !== saved.formStatus ||
          !groupsMatch
        )
      }
      return (
        current.groups.length > 0 ||
        current.formTitle !== (selectedTemplateItem.name || 'Untitled Form')
      )
    }
    return current.groups.length > 0 || current.formTitle !== 'Untitled Form'
  }

  useEffect(() => {
    const state = location.state as CreateFormLocationState | undefined
    if (state?.templateId) {
      navigate(`/form-builder/edit/${state.templateId}`, {
        replace: true,
        state: undefined,
      })
      return
    }
    if (state?.formTitle) {
      setInitialFormTitle(state.formTitle)
      const categoryTitle = state.categoryTitle ?? 'Others'
      setSelectedCategory(categoryTitle)
      const category = TEMPLATE_CATEGORIES.find(
        (c) => c.title === categoryTitle,
      )
      const matchingItem = category?.items.find(
        (item) =>
          item.name === state.formTitle ||
          item.name.toLowerCase() === state.formTitle?.toLowerCase(),
      )
      setSelectedTemplateItem(
        matchingItem
          ? { name: matchingItem.name, status: matchingItem.status }
          : null,
      )
      return
    }
    // Do not auto-select first item on load; user selects by clicking an item
  }, [templateCategories, location, routeTemplateId, navigate])

  const handleCreateNewForm = (): void => {
    setEditingTemplateId(null)
    setInitialFormTitle(null)
    setSelectedTemplateItem(null)
    setFormBuilderKey((k) => k + 1)
    if (!selectedCategory) {
      setSelectedCategory(templateCategories[0].title)
    }
    navigate('/form-builder/create')
  }

  const handleCreateForms = (): void => {
    const withItems = templateCategories.find((cat) => cat.items.length > 0)
    setSelectedCategory(withItems ? withItems.title : 'Others')
  }

  const handleTemplateItemSelect = (
    item: SelectedTemplateItem,
    categoryTitle: string,
  ): void => {
    if (item.form_template_id) {
      navigate(`/form-builder/view/${item.form_template_id}`)
      return
    }
    setSelectedTemplateItem(item)
    setSelectedCategory(categoryTitle)
  }

  const getTemplateKey = (
    categoryTitle: string,
    templateName: string,
  ): string => `${categoryTitle}|${templateName}`

  const handleTemplateItemSelectWithConfirm = (
    item: SelectedTemplateItem,
    categoryTitle: string,
  ): void => {
    if (hasUnsavedChanges()) {
      setPendingAction(
        () => () => handleTemplateItemSelect(item, categoryTitle),
      )
      setShowUnsavedConfirmModal(true)
    } else {
      handleTemplateItemSelect(item, categoryTitle)
    }
  }

  const handleCreateNewFormWithConfirm = (): void => {
    if (hasUnsavedChanges()) {
      setPendingAction(() => handleCreateNewForm)
      setShowUnsavedConfirmModal(true)
    } else {
      handleCreateNewForm()
    }
  }

  const handleCreateCategoryForm = (categoryTitle?: string): void => {
    setEditingTemplateId(null)
    setInitialFormTitle(null)
    setSelectedTemplateItem(null)
    setSelectedCategory(categoryTitle ?? null)
    setFormBuilderKey((k) => k + 1)
    navigate('/form-builder/create', {
      state: { formTitle: 'Untitled', categoryTitle },
    })
  }

  const handleCreateCategoryFormWithConfirm = (
    categoryTitle?: string,
  ): void => {
    if (hasUnsavedChanges()) {
      setPendingAction(() => () => handleCreateCategoryForm(categoryTitle))
      setShowUnsavedConfirmModal(true)
    } else {
      handleCreateCategoryForm(categoryTitle)
    }
  }

  const handleConfirmUnsavedModal = (): void => {
    if (pendingAction) {
      const run = pendingAction()
      if (typeof run === 'function') run()
      setPendingAction(null)
    }
    setShowUnsavedConfirmModal(false)
  }

  const handleCloseUnsavedModal = (): void => {
    setPendingAction(null)
    setShowUnsavedConfirmModal(false)
  }

  const handleSaveTemplate = (asDraft: boolean = false): void => {
    if (isViewMode) return
    const formData = formDataRef.current?.()
    if (!formData) return

    const categoryTitle = selectedCategory ?? 'Others'
    const templateKey = getTemplateKey(categoryTitle, formData.formTitle)

    const category = templateCategories.find((c) => c.title === categoryTitle)
    if (!category) return

    setIsSavingTemplate(true)

    const savedKey =
      selectedTemplateItem && selectedCategory
        ? getTemplateKey(selectedCategory, selectedTemplateItem.name)
        : null
    const saved = savedKey ? savedTemplateFormData[savedKey] : null
    const schemaChanged = saved
      ? JSON.stringify(formData.groups) !== JSON.stringify(saved.groups)
      : false
    const wasApproved = saved?.formStatus === FormTemplateStatus.Approved

    const effectiveStatus: FormTemplateStatus = asDraft
      ? FormTemplateStatus.Draft
      : wasApproved && schemaChanged
        ? FormTemplateStatus.Pending
        : formData.formStatus === FormTemplateStatus.Approved
          ? FormTemplateStatus.Approved
          : FormTemplateStatus.Pending

    const payload = {
      title: formData.formTitle || 'Untitled Form',
      category_title: categoryTitle,
      status: effectiveStatus,
      schema_json: formData.groups,
    }

    const sourceTemplateId = editingTemplateId ?? routeTemplateId ?? null
    const isEditMode = Boolean(routeTemplateId) && Boolean(sourceTemplateId)

    if (isEditMode && sourceTemplateId) {
      createFormTemplateVersion(sourceTemplateId, payload)
        .then((newTemplate) => {
          const newId = newTemplate.form_template_id
          if (newId) setEditingTemplateId(newId)
          const isUpdate =
            selectedTemplateItem &&
            category.items.some(
              (item) => item.name === selectedTemplateItem.name,
            )
          if (isUpdate && selectedTemplateItem) {
            const oldKey = getTemplateKey(
              categoryTitle,
              selectedTemplateItem.name,
            )
            const newKey = templateKey
            setTemplateCategories((prev) =>
              prev.map((cat) => {
                if (cat.title !== categoryTitle) return cat
                return {
                  ...cat,
                  items: cat.items.map((item) =>
                    item.name === selectedTemplateItem.name
                      ? {
                          name: formData.formTitle,
                          status: effectiveStatus,
                          icon: item.icon ?? 'form',
                          form_template_id: newId ?? item.form_template_id,
                        }
                      : item,
                  ),
                }
              }),
            )
            setSavedTemplateFormData((prev) => {
              const next = { ...prev }
              if (oldKey !== newKey) delete next[oldKey]
              next[newKey] = {
                formTitle: formData.formTitle,
                groups: formData.groups,
                formStatus: effectiveStatus,
              }
              return next
            })
            setSelectedTemplateItem({
              name: formData.formTitle,
              status: effectiveStatus,
            })
          }
          showToast(createFormTemplateUpdatedToast())
          if (newId) {
            navigate(`/form-builder/view/${newId}`)
          }
        })
        .catch((err: unknown) => {
          showToast(
            createFailedToast(
              'Failed to update form template',
              err instanceof Error ? err.message : 'Please try again.',
            ),
          )
        })
        .finally(() => setIsSavingTemplate(false))
    } else {
      createFormTemplate(payload)
        .then((newTemplate) => {
          if (newTemplate?.form_template_id) {
            const newTitle = formData.formTitle || 'Untitled Form'
            setTemplateCategories((prev) =>
              prev.map((cat) => {
                if (cat.title !== categoryTitle) return cat
                return {
                  ...cat,
                  items: cat.items.map((item) =>
                    item.name === newTitle && !item.form_template_id
                      ? {
                          ...item,
                          form_template_id: newTemplate.form_template_id,
                        }
                      : item,
                  ),
                }
              }),
            )
            setEditingTemplateId(newTemplate.form_template_id)
            navigate(`/form-builder/view/${newTemplate.form_template_id}`)
          }
          showToast(createFormTemplateCreatedToast())
        })
        .catch((err: unknown) => {
          showToast(
            createFailedToast(
              'Failed to create form template',
              err instanceof Error ? err.message : 'Please try again.',
            ),
          )
        })
        .finally(() => setIsSavingTemplate(false))
    }

    const isUpdate =
      selectedTemplateItem &&
      category.items.some((item) => item.name === selectedTemplateItem.name)

    if (isUpdate && selectedTemplateItem) {
      const oldKey = getTemplateKey(categoryTitle, selectedTemplateItem.name)
      const newKey = templateKey

      setTemplateCategories((prev) =>
        prev.map((cat) => {
          if (cat.title !== categoryTitle) return cat
          return {
            ...cat,
            items: cat.items.map((item) =>
              item.name === selectedTemplateItem.name
                ? {
                    name: formData.formTitle,
                    status: effectiveStatus,
                    icon: item.icon ?? 'form',
                    form_template_id: item.form_template_id,
                  }
                : item,
            ),
          }
        }),
      )
      setSavedTemplateFormData((prev) => {
        const next = { ...prev }
        if (oldKey !== newKey) delete next[oldKey]
        next[newKey] = {
          formTitle: formData.formTitle,
          groups: formData.groups,
          formStatus: effectiveStatus,
        }
        return next
      })
      setSelectedTemplateItem({
        name: formData.formTitle,
        status: effectiveStatus,
      })
    } else {
      setTemplateCategories((prev) =>
        prev.map((cat) => {
          if (cat.title !== categoryTitle) return cat
          const exists = cat.items.some(
            (item) =>
              item.name.toLowerCase() === formData.formTitle.toLowerCase(),
          )
          if (exists) return cat
          return {
            ...cat,
            items: [
              ...cat.items,
              {
                name: formData.formTitle || 'Untitled Form',
                status: effectiveStatus,
                icon: 'form',
              },
            ],
          }
        }),
      )
      setSavedTemplateFormData((prev) => ({
        ...prev,
        [templateKey]: {
          formTitle: formData.formTitle || 'Untitled Form',
          groups: formData.groups,
          formStatus: effectiveStatus,
        },
      }))
      setSelectedCategory(categoryTitle)
      setSelectedTemplateItem({
        name: formData.formTitle || 'Untitled Form',
        status: effectiveStatus,
      })
    }
  }

  const handleSaveAsDraft = (): void => {
    handleSaveTemplate(true)
  }

  const executeDiscard = (): void => {
    const templateId = editingTemplateId ?? routeTemplateId
    if (mode === 'edit' && templateId) {
      navigate(`/form-builder/view/${templateId}`)
      setMode('view')
      return
    }
    navigate('/form-builder/templates')
  }

  const handleDiscard = (): void => {
    if (hasUnsavedChanges()) {
      setPendingAction(() => executeDiscard)
      setShowUnsavedConfirmModal(true)
      return
    }
    executeDiscard()
  }

  const handleStatusChange = (
    templateName: string,
    categoryTitle: string,
    newStatus: FormTemplateStatus,
  ): void => {
    setTemplateCategories((prevCategories) =>
      prevCategories.map((category) => {
        if (category.title === categoryTitle) {
          return {
            ...category,
            items: category.items.map((item) =>
              item.name === templateName
                ? { ...item, status: newStatus }
                : item,
            ),
          }
        }
        return category
      }),
    )

    if (
      selectedTemplateItem?.name === templateName &&
      selectedCategory === categoryTitle
    ) {
      setSelectedTemplateItem({
        ...selectedTemplateItem,
        status: newStatus,
      })
    }
  }

  const handleTemplateApprove = (
    templateName: string,
    categoryTitle: string,
  ): void => {
    const category = templateCategories.find((c) => c.title === categoryTitle)
    const item = category?.items.find((i) => i.name === templateName)
    const templateId = item?.form_template_id

    if (templateId) {
      updateFormTemplateStatus(templateId, FormTemplateStatus.Approved)
        .then(() => {
          handleStatusChange(
            templateName,
            categoryTitle,
            FormTemplateStatus.Approved,
          )
          const templateKey = `${categoryTitle}|${templateName}`
          const current = formDataRef.current?.()
          setSavedTemplateFormData((prev) => {
            const existing = prev[templateKey]
            return {
              ...prev,
              [templateKey]: {
                formTitle:
                  current?.formTitle ?? existing?.formTitle ?? templateName,
                groups: current?.groups ?? existing?.groups ?? [],
                formStatus: FormTemplateStatus.Approved,
              },
            }
          })
          showToast(createFormTemplateApprovedToast())
        })
        .catch((err: unknown) => {
          showToast(
            createFailedToast(
              'Failed to approve form template',
              err instanceof Error ? err.message : 'Please try again.',
            ),
          )
        })
    } else {
      handleStatusChange(
        templateName,
        categoryTitle,
        FormTemplateStatus.Approved,
      )
    }
  }

  const removeTemplateFromLocalState = (
    templateName: string,
    categoryTitle: string,
  ): void => {
    setTemplateCategories((prev) =>
      prev.map((cat) => {
        if (cat.title !== categoryTitle) return cat
        return {
          ...cat,
          items: cat.items.filter((item) => item.name !== templateName),
        }
      }),
    )
    if (
      selectedTemplateItem?.name === templateName &&
      selectedCategory === categoryTitle
    ) {
      setSelectedTemplateItem(null)
    }
  }

  const handleTemplateDelete = (
    templateName: string,
    categoryTitle: string,
  ): void => {
    const category = templateCategories.find((c) => c.title === categoryTitle)
    const item = category?.items.find((i) => i.name === templateName)
    const templateId = item?.form_template_id
    const isDeletingSelectedTemplate =
      selectedTemplateItem?.name === templateName &&
      selectedCategory === categoryTitle

    const doRemoveFromState = (): void => {
      removeTemplateFromLocalState(templateName, categoryTitle)
      const templateKey = `${categoryTitle}|${templateName}`
      setSavedTemplateFormData((prev) => {
        const next = { ...prev }
        delete next[templateKey]
        return next
      })
      if (isDeletingSelectedTemplate) {
        setEditingTemplateId(null)
        setInitialFormTitle(null)
        setFormBuilderKey((k) => k + 1)
        navigate('/form-builder/create')
      }
    }

    if (templateId) {
      deleteFormTemplate(templateId)
        .then(() => {
          showToast(createFormTemplateDeletedToast())
          doRemoveFromState()
        })
        .catch((err: unknown) => {
          showToast(
            createFailedToast(
              'Failed to delete form template',
              err instanceof Error ? err.message : 'Please try again.',
            ),
          )
        })
    } else {
      doRemoveFromState()
    }
  }

  const handleTemplateMoveToDraft = (
    templateName: string,
    categoryTitle: string,
  ): void => {
    const category = templateCategories.find((c) => c.title === categoryTitle)
    const item = category?.items.find((i) => i.name === templateName)
    if (!item) return

    if (item.form_template_id) {
      updateFormTemplateStatus(item.form_template_id, FormTemplateStatus.Draft)
        .then(() => {
          showToast(
            createSuccessToast(
              'Set to draft',
              'The form template has been set to draft.',
            ),
          )
        })
        .catch((err: unknown) => {
          showToast(
            createFailedToast(
              'Failed to set to draft',
              err instanceof Error ? err.message : 'Please try again.',
            ),
          )
        })
    }

    handleStatusChange(templateName, categoryTitle, FormTemplateStatus.Draft)

    if (
      selectedTemplateItem?.name === templateName &&
      selectedCategory === categoryTitle
    ) {
      setSelectedTemplateItem({
        ...selectedTemplateItem,
        status: FormTemplateStatus.Draft,
      })
    }
  }

  const isEditLoading = Boolean(routeTemplateId && !editTemplateLoadSettled)
  const canAssignTemplate =
    Boolean(activeTemplateId) &&
    selectedTemplateItem?.status === FormTemplateStatus.Approved &&
    !isFormDirty

  return (
    <div className="create-form-container-wrapper">
      <Modal
        open={showUnsavedConfirmModal}
        onClose={handleCloseUnsavedModal}
        size="sm"
        variant="confirmation"
        title="Unsaved changes"
        description="You have unsaved changes. Are you sure you want to switch? Your changes will be lost."
        secondaryLabel="Cancel"
        primaryLabel="Confirm"
        onSecondaryClick={handleCloseUnsavedModal}
        onPrimaryClick={handleConfirmUnsavedModal}
      />
      {templateLoadError && (
        <Alert
          variant="critical"
          layout="board"
          title="Couldn't load the template"
          description={templateLoadError}
          showActions={false}
          showMenu={false}
        />
      )}
      <div className="create-form-layout">
        {isViewMode && (
          <TemplatesSidebar
            templateCategories={templateCategories}
            templateSearch={templateSearch}
            onTemplateSearchChange={setTemplateSearch}
            selectedCategory={selectedCategory}
            selectedTemplateItem={selectedTemplateItem}
            onCreateCategoryForm={handleCreateCategoryFormWithConfirm}
            onTemplateItemSelect={handleTemplateItemSelectWithConfirm}
            onTemplateApprove={handleTemplateApprove}
            onTemplateDelete={handleTemplateDelete}
            onTemplateMoveToDraft={handleTemplateMoveToDraft}
            hasUnsavedChanges={isFormDirty}
          />
        )}

        <div className="create-form-container">
          <CreateFormHeader
            showActions={shouldShowFormBuilder()}
            mode={mode}
            templateStatus={selectedTemplateItem?.status}
            previewToggleRef={previewToggleRef}
            onSaveTemplate={() => handleSaveTemplate(false)}
            onSaveAsDraft={handleSaveAsDraft}
            onDiscard={handleDiscard}
            saveInProgress={isSavingTemplate}
            onEditTemplate={() => {
              const id = editingTemplateId ?? routeTemplateId
              if (!id) return
              navigate(`/form-builder/edit/${id}`)
              setMode('edit')
            }}
            onApproveTemplate={
              selectedTemplateItem && selectedCategory
                ? () =>
                    handleTemplateApprove(
                      selectedTemplateItem.name,
                      selectedCategory,
                    )
                : undefined
            }
            onCreateNewForm={handleCreateNewFormWithConfirm}
            onSendToPatient={handleSendToPatient}
            canAssign={canAssignTemplate}
          />

          <div className="create-form-body">
            <div
              className={`form-content-transition-wrapper${
                isEditLoading ? ' form-content-transition-wrapper-loading' : ''
              }`}
            >
              {!isEditLoading && (
                <FormContentArea
                  formBuilderKey={formBuilderKey}
                  showFormBuilder={shouldShowFormBuilder()}
                  hasAnyForms={hasAnyForms()}
                  selectedCategory={selectedCategory}
                  selectedTemplateItem={selectedTemplateItem}
                  templateCategories={templateCategories}
                  initialFormTitle={initialFormTitle ?? undefined}
                  onCategorySelect={handleCategorySelect}
                  onCreateForms={handleCreateForms}
                  componentClickRef={componentClickRef}
                  previewToggleRef={previewToggleRef}
                  onStatusChange={handleStatusChange}
                  formDataRef={formDataRef}
                  savedTemplateFormData={savedTemplateFormData}
                  onFormDataChange={onFormDataChange}
                  selectedFieldId={selectedFieldId}
                  onFieldSelect={handleFieldSelect}
                  fieldSettingsRef={fieldSettingsRef}
                  readOnly={isViewMode}
                />
              )}
            </div>
          </div>
        </div>

        {!isViewMode && (
          <FormComponentsSidebar
            selectedField={selectedField}
            onFieldSettingsChange={handleFieldSettingsChange}
            onComponentClick={(component) => {
              if (componentClickRef.current) {
                componentClickRef.current(component)
              }
            }}
            readOnly={isViewMode}
          />
        )}
      </div>
      <AssignFormsModal
        show={showAssignFormsModal}
        onHide={() => setShowAssignFormsModal(false)}
        patientName={selectedPatientName}
        patientId={selectedPatient?.patient_id}
        publicPatientId={selectedPatient?.public_id}
        patientMobile={selectedContact?.cellphone ?? selectedPatient?.cellphone}
        initialSelectedFormId={activeTemplateId ?? undefined}
        onFormsAssigned={onFormsAssigned}
      />
      {!selectedPatient?.patient_id && showSelectPatientModal && (
        <SelectPatientModal
          isOpen={showSelectPatientModal}
          onSelectPatient={handleSelectPatientForAssignment}
          onHide={() => setShowSelectPatientModal(false)}
        />
      )}
    </div>
  )
}

export default CreateForm
